import jsPDF from "jspdf";
import "jspdf-autotable";

export const generateInvoicePDF = (invoiceData) => {
  const doc = new jsPDF();

  // Page dimensions
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;

  // Colors - Black and white only
  const black = [0, 0, 0];

  // Helper functions
  const formatCurrency = (amount) => {
    return `Rs. ${new Intl.NumberFormat("en-IN").format(amount || 0)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return "N/A";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    return `${dd}-${mm}-${date.getFullYear()}`;
  };

  let yPos = margin;

  // ===== HEADER SECTION =====
  doc.setTextColor(...black);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);

  // Company name centered - all caps and bold
  const companyName = "ROAD PILOT TECHNOLOGIES PRIVATE LIMITED";
  const companyNameWidth = doc.getTextWidth(companyName);
  doc.text(companyName, (pageWidth - companyNameWidth) / 2, yPos);

  yPos += 10;

  // Address centered - all caps and bold, slightly smaller
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  const address = "E-1475, OMAXE CITY, R.B. ROAD, LUCKNOW, U.P. - 226002";
  const addressWidth = doc.getTextWidth(address);
  doc.text(address, (pageWidth - addressWidth) / 2, yPos);

  yPos += 15;

  // Contact details line
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const contactLeft = "Contact No.: 1800-571-55-99";
  const contactRight = "Email Id: info@roadpilot.co.in";
  doc.text(contactLeft, margin, yPos);
  const contactRightWidth = doc.getTextWidth(contactRight);
  doc.text(contactRight, pageWidth - margin - contactRightWidth, yPos);

  yPos += 20;

  // ===== TAX INVOICE SECTION =====
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  const taxInvoiceTitle = "TAX INVOICE";
  const taxInvoiceTitleWidth = doc.getTextWidth(taxInvoiceTitle);
  doc.text(taxInvoiceTitle, (pageWidth - taxInvoiceTitleWidth) / 2, yPos);

  yPos += 20;

  // Left side - Invoice number
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const invoiceNumber = invoiceData.invoice_number || invoiceData.invoiceNumber || "N/A";
  doc.text(`Invoice No.: ${invoiceNumber}`, margin, yPos);

  // Below invoice number - "Billed to:"
  yPos += 10;
  const clientName = invoiceData.client?.name || invoiceData.client_name || invoiceData.username || "N/A";
  doc.text(
    `Billed to: ${clientName}`,
    margin,
    yPos
  );

  // Right side - Original for Recipient, Invoice Date, GSTIN
  yPos -= 10; // Reset to same level as Invoice No.
  const rightColX = pageWidth - margin - 80;
  doc.text("(Original for Recipient)", rightColX, yPos);
  yPos += 8;
  doc.text(
    `Invoice Date: ${formatDate(
      invoiceData.Invoice_date || invoiceData.invoice_date
    )}`,
    rightColX,
    yPos
  );
  yPos += 8;
  doc.text("GSTIN: 09AALCR3424M1ZX", rightColX, yPos);

  yPos += 20;

  // ===== MAIN CONTENT RECTANGLE =====
  // Draw the main black bordered rectangle
  doc.setDrawColor(...black);
  doc.setLineWidth(1);
  const rectStartY = yPos;
  const rectHeight = 180; // Adjust based on content
  doc.rect(margin, rectStartY, pageWidth - 2 * margin, rectHeight);

  // Content inside rectangle - with padding
  const rectPadding = 10;
  const contentStartX = margin + rectPadding;
  yPos += rectPadding;

  // Name
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(
    `Name: ${invoiceData.client?.name || invoiceData.client_name || invoiceData.username || "N/A"}`,
    contentStartX,
    yPos
  );
  yPos += 10;

  // Address
  const clientAddress = invoiceData.client?.address || invoiceData.shipping_address || "N/A";
  doc.text(
    `Address: ${clientAddress}`,
    contentStartX,
    yPos
  );
  yPos += 15;

  // State and State Code on same line
  const state = invoiceData.state || invoiceData.client?.state || "N/A";
  doc.text(`State: ${state}`, contentStartX, yPos);
  const rawStateCode = invoiceData.state_code || invoiceData.client?.tax_number || "";
  const stateCode = rawStateCode && rawStateCode.length >= 2 ? rawStateCode.slice(0, 2) : "N/A";
  const stateCodeText = `State Code: ${stateCode}`;
  const stateCodeWidth = doc.getTextWidth(stateCodeText);
  doc.text(
    stateCodeText,
    pageWidth - margin - rectPadding - stateCodeWidth,
    yPos
  );
  yPos += 10;

  // GSTIN
  const gstin = invoiceData.client_gstin || invoiceData.client?.tax_number || invoiceData.state_code || "";
  doc.text(`GSTIN: ${gstin}`, contentStartX, yPos);
  yPos += 15;

  // ===== ITEMS TABLE INSIDE RECTANGLE =====
  if (invoiceData.items && invoiceData.items.length > 0) {
    // Table headers
    const tableStartY = yPos;
    const rowHeight = 12;

    // Table structure - adjusted for rectangle
    const tableMargin = contentStartX;
    const tableWidth = pageWidth - 2 * margin - 2 * rectPadding;
    const col1Width = 25;
    const col2Width = tableWidth * 0.5;
    const col3Width = 25;
    const col4Width = tableWidth - col1Width - col2Width - col3Width;

    const col1X = tableMargin;
    const col2X = col1X + col1Width;
    const col3X = col2X + col2Width;
    const col4X = col3X + col3Width;

    // Draw table borders
    doc.setLineWidth(0.3);
    doc.rect(col1X, tableStartY, tableWidth, rowHeight); // Header row

    // Header text
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("S.No.", col1X + 2, tableStartY + 8);
    doc.text("Description of Service", col2X + 2, tableStartY + 8);
    doc.text("QTY", col3X + 2, tableStartY + 8);
    doc.text("Amount (Rs.)", col4X + 2, tableStartY + 8);

    // Vertical lines for header
    doc.line(col2X, tableStartY, col2X, tableStartY + rowHeight);
    doc.line(col3X, tableStartY, col3X, tableStartY + rowHeight);
    doc.line(col4X, tableStartY, col4X, tableStartY + rowHeight);

    yPos += rowHeight;

    // Table rows
    doc.setFont("helvetica", "normal");
    const itemsArray = invoiceData.items || [];
    itemsArray.forEach((item, index) => {
      const currentRowY = yPos;
      const itemRowHeight = 15;

      // Draw row border
      doc.rect(col1X, currentRowY, tableWidth, itemRowHeight);

      // Vertical lines
      doc.line(col2X, currentRowY, col2X, currentRowY + itemRowHeight);
      doc.line(col3X, currentRowY, col3X, currentRowY + itemRowHeight);
      doc.line(col4X, currentRowY, col4X, currentRowY + itemRowHeight);

      // Row content
      doc.text((index + 1).toString(), col1X + 2, currentRowY + 8);

      // Description
      const description =
        item.name || item.description || item.item_name || "Service";
      const descLines = doc.splitTextToSize(description, col2Width - 4);
      doc.text(descLines[0], col2X + 2, currentRowY + 8);

      // Quantity
      const quantity = item.quantity || item.qty || 1;
      doc.text(
        quantity.toString(),
        col3X + 2,
        currentRowY + 8
      );

      // Amount (right aligned)
      const unitPrice = item.unit_price || item.price || item.rate || 0;
      const itemTotal = quantity * unitPrice;
      const amount = formatCurrency(itemTotal);
      const amountWidth = doc.getTextWidth(amount);
      doc.text(amount, col4X + col4Width - amountWidth - 2, currentRowY + 8);

      yPos += itemRowHeight;
    });

    // Totals section
    const totalsRowHeight = 12;

    // Taxable Value
    doc.rect(col1X, yPos, tableWidth, totalsRowHeight);
    doc.line(col3X, yPos, col3X, yPos + totalsRowHeight);
    doc.line(col4X, yPos, col4X, yPos + totalsRowHeight);

    doc.setFont("helvetica", "bold");
    doc.text("Taxable Value", col1X + 2, yPos + 8);
    const totalAmount = invoiceData.total_amount || invoiceData.totals?.total || 0;
    const taxableAmount = formatCurrency(totalAmount / 1.18);
    const taxableAmountWidth = doc.getTextWidth(taxableAmount);
    doc.text(
      taxableAmount,
      col4X + col4Width - taxableAmountWidth - 2,
      yPos + 8
    );

    yPos += totalsRowHeight;

    // CGST @ 9%
    doc.rect(col1X, yPos, tableWidth, totalsRowHeight);
    doc.line(col3X, yPos, col3X, yPos + totalsRowHeight);
    doc.line(col4X, yPos, col4X, yPos + totalsRowHeight);

    doc.setFont("helvetica", "normal");
    doc.text("Add: CGST @ 9%", col1X + 2, yPos + 8);
    const cgstAmount = formatCurrency((totalAmount / 1.18) * 0.09);
    const cgstAmountWidth = doc.getTextWidth(cgstAmount);
    doc.text(cgstAmount, col4X + col4Width - cgstAmountWidth - 2, yPos + 8);

    yPos += totalsRowHeight;

    // SGST @ 9%
    doc.rect(col1X, yPos, tableWidth, totalsRowHeight);
    doc.line(col3X, yPos, col3X, yPos + totalsRowHeight);
    doc.line(col4X, yPos, col4X, yPos + totalsRowHeight);

    doc.text("Add: SGST @ 9%", col1X + 2, yPos + 8);
    const sgstAmount = formatCurrency((totalAmount / 1.18) * 0.09);
    const sgstAmountWidth = doc.getTextWidth(sgstAmount);
    doc.text(sgstAmount, col4X + col4Width - sgstAmountWidth - 2, yPos + 8);

    yPos += totalsRowHeight;

    // Total Invoice Value
    doc.rect(col1X, yPos, tableWidth, totalsRowHeight);
    doc.line(col3X, yPos, col3X, yPos + totalsRowHeight);
    doc.line(col4X, yPos, col4X, yPos + totalsRowHeight);

    doc.setFont("helvetica", "bold");
    doc.text("Total Invoice Value", col1X + 2, yPos + 8);
    const totalAmountFormatted = formatCurrency(totalAmount);
    const totalAmountWidth = doc.getTextWidth(totalAmountFormatted);
    doc.text(totalAmountFormatted, col4X + col4Width - totalAmountWidth - 2, yPos + 8);

    yPos += totalsRowHeight + 10;

    // ===== SIGNATURE SECTION (INSIDE RECTANGLE) =====
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(
      "For Road Pilot Technologies Private Limited",
      contentStartX,
      yPos
    );

    yPos += 20; // Space for signature

    doc.text("(Authorised Signatory)", contentStartX, yPos);
    yPos += 15;

    // Payment terms
    doc.setFont("helvetica", "bold");
    doc.text("Payment terms :", contentStartX, yPos);
    yPos += 8;
    doc.setFont("helvetica", "normal");
    doc.text(
      "1. 50% While Placing Order/signing of MOU, balance on deputation of Drivers.",
      contentStartX,
      yPos
    );

    // Move outside rectangle for bank details
    yPos = rectStartY + rectHeight + 15;
  }

  // ===== BANK DETAILS FOOTER (OUTSIDE RECTANGLE) =====
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  const bankDetailsTitle = "Our Bank Details: Road Pilot Technologies";
  const bankDetailsTitleWidth = doc.getTextWidth(bankDetailsTitle);
  doc.text(bankDetailsTitle, (pageWidth - bankDetailsTitleWidth) / 2, yPos);

  yPos += 8;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const bankDetails =
    "Private Limited, A/c No. 29040200011171 Bank Of Baroda, IFSC: BARB0VIBHUT";
  const bankDetailsWidth = doc.getTextWidth(bankDetails);
  doc.text(bankDetails, (pageWidth - bankDetailsWidth) / 2, yPos);

  // Return the PDF as a blob instead of saving
  return doc.output('blob');
};
